import React, { useState, useRef, useEffect } from 'react';
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Box from '@mui/material/Box';
import Modal from '@mui/material/Modal';
import CloseIcon from "@mui/icons-material/Close";
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DrawIcon from '@mui/icons-material/Draw';
import { DrawOutlined as DrawOutlinedIcon } from "@mui/icons-material";
import DownloadIcon from '@mui/icons-material/Download';
import TextFieldsIcon from '@mui/icons-material/TextFields';
import DeleteIcon from '@mui/icons-material/Delete';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import SignatureCanvas from 'react-signature-canvas';
import { PDFDocument } from 'pdf-lib';
import LoadSpinner from '../../../utilities/LoadSpinner';
import axios from 'axios';
import {
  API_post_file_upload_with_eagerly_generated_presigned_url,
  API_get_file_upload
} from '../../../constants';
import { getDefaultAxiosConfig, getRightworkAPI } from '../requests/utilities/requests';
import { useNotifyMobileModal } from '../take_training_employee/useNotifyMobileModal';

function SignatureModal(props) {
  useNotifyMobileModal(props.open);

  // PDF State
  const [pdfFile, setPdfFile] = useState(null);
  const [pdfBytes, setPdfBytes] = useState(null);
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState(null);
  const [pdfFileUuid, setPdfFileUuid] = useState(null);
  const [pdfFileUrl, setPdfFileUrl] = useState(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  // Real per-page dimensions in PDF points (from pdf-lib). Drives the preview's
  // aspect ratio and field scale so coordinates line up on any page size, not
  // just US Letter portrait. [{width, height}, ...] indexed by page.
  const [pageDimensions, setPageDimensions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  // Signed documents are kept on the employee profile by default.
  const [saveToProfile, setSaveToProfile] = useState(true);

  // Field Placement State
  const [fieldMode, setFieldMode] = useState('signature');
  const [placedFields, setPlacedFields] = useState([]);
  const [activeFieldId, setActiveFieldId] = useState(null);
  const [containerScale, setContainerScale] = useState(1);

  // Drag State
  const [draggingFieldId, setDraggingFieldId] = useState(null);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [mouseDownPos, setMouseDownPos] = useState({ x: 0, y: 0 });

  // Modal State
  const [showSignaturePad, setShowSignaturePad] = useState(false);
  const [showTextInput, setShowTextInput] = useState(false);
  const [textInputValue, setTextInputValue] = useState('');

  // Refs
  const sigCanvas = useRef(null);
  const pdfPreviewRef = useRef(null);
  const pdfContainerRef = useRef(null);

  // The page currently shown — its real point dimensions drive the preview's
  // aspect ratio (falls back to US Letter before the PDF has loaded).
  const currentPageSize = pageDimensions[currentPage] || { width: 612, height: 792 };

  const style = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: '88%',
    maxWidth: 1320,
    height: '95vh',
    maxHeight: '98vh',
    bgcolor: "background.paper",
    border: "2px solid #E2E8F0",
    borderRadius: "15px",
    boxShadow: 24,
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
  };

  // Load existing data when modal opens
  useEffect(() => {
    const loadExistingPdf = async () => {
      if (props.open && props.existingData?.file_uuid) {
        setPdfFileUuid(props.existingData.file_uuid);
        setPlacedFields(props.existingData.fieldLocations || []);
        setTotalPages(props.existingData.totalPages || 1);
        setSaveToProfile(
          props.existingData.saveToProfile !== undefined
            ? props.existingData.saveToProfile
            : true
        );
        setCurrentPage(0);

        // Fetch fresh presigned URL from file_uuid
        try {
          const resFile = await getRightworkAPI(API_get_file_upload, {
            file_upload_uuid: props.existingData.file_uuid,
          });
          const freshUrl = resFile?.data?.redux?.payload?.presigned_url;

          if (!freshUrl) {
            throw new Error('Could not get presigned URL');
          }

          setPdfFileUrl(freshUrl);

          // Fetch the PDF and create a blob URL for preview
          const response = await fetch(freshUrl);
          const arrayBuffer = await response.arrayBuffer();
          setPdfBytes(arrayBuffer);

          // Capture real per-page dimensions for correct preview proportions.
          const existingDoc = await PDFDocument.load(arrayBuffer);
          setPageDimensions(existingDoc.getPages().map((p) => p.getSize()));

          // Create blob URL for preview
          const blob = new Blob([arrayBuffer], { type: 'application/pdf' });
          const blobUrl = URL.createObjectURL(blob);
          setPdfPreviewUrl(blobUrl);
        } catch (error) {
          console.error('Error loading existing PDF:', error);
          toast.error('Failed to load existing PDF. Please try again.');
        }
      }
    };

    loadExistingPdf();
  }, [props.open, props.existingData]);

  // Track container size for field scaling. Field base sizes are in PDF points;
  // scaling by (containerWidth / pageWidthInPoints) makes a point-sized field
  // render at its true fraction of the page on screen AND match the coordinates
  // pdf-lib burns into the output exactly — for any page size, not just Letter.
  useEffect(() => {
    if (!pdfContainerRef.current) return;

    const updateScale = () => {
      if (pdfContainerRef.current) {
        const containerWidth = pdfContainerRef.current.offsetWidth;
        const pageWidth = (pageDimensions[currentPage]?.width) || 612;
        setContainerScale(containerWidth / pageWidth);
      }
    };

    // Initial calculation
    updateScale();

    // Watch for resize
    const resizeObserver = new ResizeObserver(updateScale);
    resizeObserver.observe(pdfContainerRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, [pdfPreviewUrl, props.open, currentPage, pageDimensions]); // Re-run on page change too

  // Upload PDF template to S3
  const uploadPdfTemplate = async (file) => {
    const formData = new FormData();
    formData.append("company_id", props.homeCompany);
    formData.append("file_name", file.name);
    formData.append("type", "lms_signature_pdf_template");

    // [1] Get presigned URL
    const axiosConfig = await getDefaultAxiosConfig();
    const res = await axios.post(
      API_post_file_upload_with_eagerly_generated_presigned_url,
      formData,
      {
        headers: {
          ...axiosConfig.headers,
          "Content-Type": "multipart/form-data",
        },
      }
    );
    const fileUUID = res?.data?.redux?.payload?.uuid;
    const presignedUrl = res?.data?.redux?.payload?.presigned_url;

    // [2] Upload file to S3
    await axios.put(presignedUrl, file, {
      headers: {
        "Content-Type": file.type,
      },
    });

    // [3] Get file URL
    const resFile = await getRightworkAPI(API_get_file_upload, {
      file_upload_uuid: fileUUID,
    });

    const fileUrl = resFile?.data?.redux?.payload?.presigned_url;

    return {
      uuid: fileUUID,
      url: fileUrl,
      name: file.name
    };
  };

  // Handle PDF Upload
  const handlePdfUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (file.type !== 'application/pdf') {
      toast.error('Please upload a PDF file only');
      return;
    }

    setIsLoading(true);

    try {
      // Read file as ArrayBuffer
      const arrayBuffer = await file.arrayBuffer();
      setPdfBytes(arrayBuffer);

      // Create blob URL for immediate preview
      const blobUrl = URL.createObjectURL(file);
      setPdfPreviewUrl(blobUrl);
      setPdfFile(file);

      // Get page count + real per-page dimensions so the preview proportions
      // and field scale match the actual PDF (not a Letter-portrait assumption).
      const pdfDoc = await PDFDocument.load(arrayBuffer);
      const pages = pdfDoc.getPages();
      setTotalPages(pages.length);
      setPageDimensions(pages.map((p) => p.getSize()));
      setCurrentPage(0);

      // Reset fields
      setPlacedFields([]);

      // Upload PDF to S3 in the background
      const uploadedFile = await uploadPdfTemplate(file);
      setPdfFileUuid(uploadedFile.uuid);
      setPdfFileUrl(uploadedFile.url);

    } catch (error) {
      console.error('Error loading PDF:', error);
      toast.error('Failed to load PDF. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Handle PDF Click to Place Field
  const handlePdfClick = (event) => {
    if (!pdfPreviewRef.current) return;

    const rect = pdfPreviewRef.current.getBoundingClientRect();
    const clickX = event.clientX - rect.left;
    const clickY = event.clientY - rect.top;

    // Field dimensions (base size that will be stored and used in PDF generation)
    const fieldWidth = fieldMode === 'signature' ? 100 : 120;
    const fieldHeight = fieldMode === 'signature' ? 50 : 30;

    // Calculate the scaled dimensions (what the user actually sees on screen)
    const scaledWidth = fieldWidth * containerScale;
    const scaledHeight = fieldHeight * containerScale;

    // Calculate position - the top-left of the field is at the click point
    const xPercent = clickX / rect.width;
    const yPercent = clickY / rect.height;

    // Clamp position using SCALED dimensions (matching what the user sees)
    // This ensures the visual representation stays fully within the preview
    const maxX = 1 - (scaledWidth / rect.width);
    const maxY = 1 - (scaledHeight / rect.height);
    const clampedX = Math.max(0, Math.min(maxX, xPercent));
    const clampedY = Math.max(0, Math.min(maxY, yPercent));

    // Create new field (store base dimensions, scaling happens at render time)
    const newField = {
      id: `field_${Date.now()}_${Math.random()}`,
      type: fieldMode,
      page: currentPage,
      x: clampedX,
      y: clampedY,
      width: fieldWidth,
      height: fieldHeight,
      value: null,
      filled: false,
    };

    setPlacedFields([...placedFields, newField]);
  };

  // Open Field Editor
  const openFieldEditor = (fieldId) => {
    const field = placedFields.find(f => f.id === fieldId);
    if (!field) return;

    setActiveFieldId(fieldId);
    if (field.type === 'signature') {
      setShowSignaturePad(true);
      // If there's an existing signature, load it into the canvas
      if (field.value && sigCanvas.current) {
        // Wait for the modal to render, then load the signature
        setTimeout(() => {
          if (sigCanvas.current) {
            // Try passing options to scale the image properly
            sigCanvas.current.fromDataURL(field.value, { ratio: 1, width: 450, height: 200 });
          }
        }, 100);
      }
    } else {
      setTextInputValue(field.value || '');
      setShowTextInput(true);
    }
  };

  // Save Signature to Field
  const saveSignatureToField = () => {
    if (!sigCanvas.current || sigCanvas.current.isEmpty()) {
      toast.error('Please draw a signature first');
      return;
    }

    const signatureDataUrl = sigCanvas.current.toDataURL('image/png');

    setPlacedFields(placedFields.map(field =>
      field.id === activeFieldId
        ? { ...field, value: signatureDataUrl, filled: true }
        : field
    ));

    setShowSignaturePad(false);
    setActiveFieldId(null);
    sigCanvas.current.clear();
  };

  // Save Text to Field
  const saveTextToField = () => {
    if (!textInputValue.trim()) {
      toast.error('Please enter text first');
      return;
    }

    setPlacedFields(placedFields.map(field =>
      field.id === activeFieldId
        ? { ...field, value: textInputValue, filled: true }
        : field
    ));

    setShowTextInput(false);
    setActiveFieldId(null);
    setTextInputValue('');
  };

  // Remove Field
  const removeField = (fieldId) => {
    setPlacedFields(placedFields.filter(field => field.id !== fieldId));
  };

  // Clear All Fields
  const clearAllFields = () => {
    if (window.confirm('Are you sure you want to remove all fields?')) {
      setPlacedFields([]);
    }
  };

  // Get Fields for Current Page
  const getFieldsByPage = (pageNumber) => {
    return placedFields.filter(field => field.page === pageNumber);
  };

  // Change Page
  const changePage = (direction) => {
    if (direction === 'next' && currentPage < totalPages - 1) {
      setCurrentPage(currentPage + 1);
    } else if (direction === 'prev' && currentPage > 0) {
      setCurrentPage(currentPage - 1);
    }
  };

  // Drag Handlers
  const handleFieldMouseDown = (event, fieldId) => {
    event.stopPropagation();
    if (!pdfPreviewRef.current) return;

    const rect = pdfPreviewRef.current.getBoundingClientRect();
    const field = placedFields.find(f => f.id === fieldId);

    if (!field) return;

    // Calculate offset from the mouse to the field's top-left corner
    const fieldLeft = field.x * rect.width;
    const fieldTop = field.y * rect.height;
    const mouseX = event.clientX - rect.left;
    const mouseY = event.clientY - rect.top;

    setDragOffset({
      x: mouseX - fieldLeft,
      y: mouseY - fieldTop
    });
    setMouseDownPos({ x: event.clientX, y: event.clientY });
    setDraggingFieldId(fieldId);
    setIsDragging(false); // Reset dragging flag
  };

  const handleMouseMove = (event) => {
    if (!draggingFieldId || !pdfPreviewRef.current) return;

    // Check if mouse has moved enough to be considered a drag (5px threshold)
    const deltaX = Math.abs(event.clientX - mouseDownPos.x);
    const deltaY = Math.abs(event.clientY - mouseDownPos.y);
    const dragThreshold = 5;

    if (!isDragging && (deltaX > dragThreshold || deltaY > dragThreshold)) {
      setIsDragging(true);
    }

    // Only update position if we're actually dragging
    if (isDragging) {
      const rect = pdfPreviewRef.current.getBoundingClientRect();
      const mouseX = event.clientX - rect.left;
      const mouseY = event.clientY - rect.top;

      // Calculate new position accounting for drag offset
      const newX = (mouseX - dragOffset.x) / rect.width;
      const newY = (mouseY - dragOffset.y) / rect.height;

      // Get the field being dragged to account for its dimensions
      const draggedField = placedFields.find(f => f.id === draggingFieldId);
      if (!draggedField) return;

      // Calculate the scaled dimensions (what the user actually sees on screen)
      const scaledWidth = draggedField.width * containerScale;
      const scaledHeight = draggedField.height * containerScale;

      // Calculate max positions using SCALED dimensions (matching what the user sees)
      // This ensures the visual representation stays fully within the preview
      const maxX = 1 - (scaledWidth / rect.width);
      const maxY = 1 - (scaledHeight / rect.height);

      // Clamp values to keep entire field within bounds (not just top-left corner)
      const clampedX = Math.max(0, Math.min(maxX, newX));
      const clampedY = Math.max(0, Math.min(maxY, newY));

      setPlacedFields(placedFields.map(field =>
        field.id === draggingFieldId
          ? { ...field, x: clampedX, y: clampedY }
          : field
      ));
    }
  };

  const handleMouseUp = (fieldId) => {
    // If we didn't actually drag, treat it as a click to open the editor
    if (draggingFieldId && !isDragging) {
      openFieldEditor(draggingFieldId);
    }

    setDraggingFieldId(null);
    setDragOffset({ x: 0, y: 0 });
    setIsDragging(false);
    setMouseDownPos({ x: 0, y: 0 });
  };

  // Add global mouse event listeners for dragging
  useEffect(() => {
    if (draggingFieldId) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);

      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [draggingFieldId, dragOffset, placedFields, isDragging, mouseDownPos]);

  // Handle Save - Stub API call
  const handleSave = async () => {
    if (!pdfPreviewUrl) {
      toast.error('Please upload a PDF first');
      return;
    }

    if (placedFields.length === 0) {
      toast.error('Please place at least one field on the PDF');
      return;
    }

    if (!pdfFileUrl) {
      toast.error('PDF is still uploading. Please wait a moment and try again.');
      return;
    }

    setIsLoading(true);

    try {
      // Save signature configuration data
      // Note: We only save file_uuid, not the presigned URL which expires
      const signatureData = {
        pdfName: pdfFile?.name || props.existingData?.pdfName || 'Document.pdf',
        file_uuid: pdfFileUuid,
        fieldLocations: placedFields,
        totalPages: totalPages,
        saveToProfile: saveToProfile,
      };

      props.handleSignatureDataSave(signatureData);
      props.handleClose();

      // Reset state
      setPdfFile(null);
      setPdfBytes(null);
      setPdfPreviewUrl(null);
      setPdfFileUuid(null);
      setPdfFileUrl(null);
      setPlacedFields([]);
      setCurrentPage(0);
      setTotalPages(0);
      setPageDimensions([]);

    } catch (error) {
      console.error('Error saving signature block:', error);
      toast.error('Failed to save signature block. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };


  // Handle Cancel
  const handleCancel = () => {
    props.handleClose();

    // Reset to existing data or clear
    if (!props.existingData?.pdfUrl) {
      setPdfFile(null);
      setPdfBytes(null);
      setPdfPreviewUrl(null);
      setPdfFileUuid(null);
      setPdfFileUrl(null);
      setPlacedFields([]);
      setCurrentPage(0);
      setTotalPages(0);
      setPageDimensions([]);
    }
  };

  return (
    <Modal
      open={props.open}
      onClose={handleCancel}
      aria-labelledby="signature-modal-title"
    >
      <Box sx={style}>
        {!isLoading ? (
          <>
            {/* Header */}
            <div className="bg-white border-b border-gray-200 px-6 py-4 shrink-0">
              <div className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-10 h-10 bg-violet-100 rounded-lg flex items-center justify-center shrink-0">
                    <DrawOutlinedIcon className="text-violet-600" style={{ fontSize: "20px" }} />
                  </div>
                  <div className="min-w-0">
                    <h3 className="text-xl font-bold text-slate-900">
                      Configure Signature Block
                    </h3>
                    <p className="text-sm text-slate-600">
                      Upload a PDF and place signature or text fields
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {pdfPreviewUrl && (
                    <div className="hidden sm:flex items-center gap-1 bg-slate-50 border border-slate-200 rounded-lg pl-3 pr-1 py-1 max-w-[300px]">
                      <span className="text-sm text-slate-700 truncate">
                        {pdfFile?.name || props.existingData?.pdfName || 'Document.pdf'}
                      </span>
                      <label className="text-xs font-medium text-violet-700 hover:bg-violet-50 cursor-pointer px-2 py-1 rounded shrink-0">
                        Replace
                        <input type="file" accept="application/pdf" onChange={handlePdfUpload} className="hidden" />
                      </label>
                    </div>
                  )}
                  <button
                    className="w-8 h-8 rounded-lg hover:bg-gray-100 flex items-center justify-center text-gray-500 hover:text-gray-700 transition-colors"
                    onClick={handleCancel}
                  >
                    <CloseIcon />
                  </button>
                </div>
              </div>
            </div>

            {/* Body */}
            {!pdfPreviewUrl ? (
              // Empty state — upload prompt
              <div className="flex-1 flex items-center justify-center p-10">
                <label className="w-full max-w-xl flex flex-col items-center justify-center gap-3 px-6 py-14 bg-violet-50 hover:bg-violet-100 text-violet-700 rounded-xl cursor-pointer border-2 border-dashed border-violet-300 transition-colors">
                  <CloudUploadIcon style={{ fontSize: 40 }} />
                  <span className="text-base font-medium">Choose PDF file</span>
                  <span className="text-xs text-violet-500">Upload the document employees will sign</span>
                  <input type="file" accept="application/pdf" onChange={handlePdfUpload} className="hidden" />
                </label>
              </div>
            ) : (
              <div className="flex-1 flex overflow-hidden">
                {/* Left rail: field tools + placed fields */}
                <div className="w-[248px] shrink-0 border-r border-slate-200 bg-slate-50 overflow-y-auto p-4 space-y-6">
                  <div>
                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Fields</p>
                    <div className="space-y-2">
                      <button
                        onClick={() => setFieldMode('signature')}
                        className={`w-full px-3 py-2.5 rounded-lg border flex items-center gap-2 transition-all ${
                          fieldMode === 'signature'
                            ? 'bg-violet-700 text-white border-violet-700'
                            : 'bg-white text-slate-700 border-slate-300 hover:border-violet-400'
                        }`}
                      >
                        <DrawIcon style={{ fontSize: 18 }} />
                        <span className="font-medium text-sm">Signature</span>
                      </button>
                      <button
                        onClick={() => setFieldMode('text')}
                        className={`w-full px-3 py-2.5 rounded-lg border flex items-center gap-2 transition-all ${
                          fieldMode === 'text'
                            ? 'bg-violet-700 text-white border-violet-700'
                            : 'bg-white text-slate-700 border-slate-300 hover:border-violet-400'
                        }`}
                      >
                        <TextFieldsIcon style={{ fontSize: 18 }} />
                        <span className="font-medium text-sm">Text field</span>
                      </button>
                    </div>
                    <p className="text-xs text-slate-500 mt-2">
                      Click the page to place a {fieldMode === 'signature' ? 'signature' : 'text'} field.
                    </p>
                  </div>

                  {placedFields.length > 0 && (
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
                          Placed ({placedFields.length})
                        </p>
                        <button
                          onClick={clearAllFields}
                          className="text-xs font-medium text-red-600 hover:text-red-700"
                        >
                          Clear all
                        </button>
                      </div>
                      <div className="space-y-1">
                        {placedFields.map((field) => (
                          <div
                            key={field.id}
                            onClick={() => setCurrentPage(field.page)}
                            className="group flex items-center gap-2 px-2 py-1.5 rounded-md border border-transparent hover:border-slate-200 hover:bg-white cursor-pointer"
                          >
                            {field.type === 'signature' ? (
                              <DrawIcon style={{ fontSize: 16 }} className="text-violet-600 shrink-0" />
                            ) : (
                              <TextFieldsIcon style={{ fontSize: 16 }} className="text-violet-600 shrink-0" />
                            )}
                            <span className="text-sm text-slate-700 flex-1 truncate">
                              {field.type === 'signature' ? 'Signature' : 'Text'}
                            </span>
                            <span className="text-xs text-slate-400 shrink-0">Pg {field.page + 1}</span>
                            <button
                              onClick={(e) => { e.stopPropagation(); removeField(field.id); }}
                              className="opacity-0 group-hover:opacity-100 text-slate-400 hover:text-red-600 transition-opacity shrink-0"
                            >
                              <DeleteIcon style={{ fontSize: 16 }} />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Right: page toolbar + canvas */}
                <div className="flex-1 flex flex-col overflow-hidden bg-slate-100">
                  <div className="flex items-center justify-center gap-4 px-4 py-2 border-b border-slate-200 bg-white shrink-0">
                    <button
                      onClick={() => changePage('prev')}
                      disabled={currentPage === 0}
                      className={`p-1 rounded ${
                        currentPage === 0 ? 'text-slate-300 cursor-not-allowed' : 'text-violet-700 hover:bg-violet-100'
                      }`}
                    >
                      <NavigateBeforeIcon />
                    </button>
                    <span className="text-sm font-medium text-slate-700">
                      Page {currentPage + 1} of {totalPages || 1}
                    </span>
                    <button
                      onClick={() => changePage('next')}
                      disabled={currentPage >= totalPages - 1}
                      className={`p-1 rounded ${
                        currentPage >= totalPages - 1 ? 'text-slate-300 cursor-not-allowed' : 'text-violet-700 hover:bg-violet-100'
                      }`}
                    >
                      <NavigateNextIcon />
                    </button>
                  </div>

                  <div className="flex-1 overflow-auto p-6 flex justify-center">
                    <div className="w-full" style={{ maxWidth: 760 }}>

                    {/* PDF Preview Container */}
                    <div
                      ref={pdfContainerRef}
                      className="relative border border-slate-300 rounded-lg bg-white shadow-sm"
                      style={{
                        width: '100%',
                        // Match the real page proportions so the embedded PDF
                        // fills the box edge-to-edge and percentage coordinates
                        // map correctly (no minHeight — it would letterbox
                        // landscape pages and break the mapping).
                        aspectRatio: `${currentPageSize.width} / ${currentPageSize.height}`,
                        overflow: 'hidden', // Prevent fields from visually escaping container
                        isolation: 'isolate' // Create stacking context
                      }}
                    >
                      <object
                        key={`pdf-page-${currentPage}`}
                        data={`${pdfPreviewUrl}#page=${currentPage + 1}&toolbar=0&navpanes=0&scrollbar=0&view=FitH`}
                        type="application/pdf"
                        style={{
                          width: '100%',
                          height: '100%',
                          border: 'none',
                          display: 'block'
                        }}
                      >
                        <iframe
                          src={`${pdfPreviewUrl}#page=${currentPage + 1}&toolbar=0&navpanes=0&scrollbar=0&view=FitH`}
                          type="application/pdf"
                          style={{
                            width: '100%',
                            height: '100%',
                            border: 'none'
                          }}
                        />
                      </object>

                      {/* Invisible overlay to capture clicks and block PDF interaction */}
                      <div
                        ref={pdfPreviewRef}
                        onClick={handlePdfClick}
                        className="absolute inset-0"
                        style={{
                          cursor: fieldMode === 'signature' ? 'crosshair' : 'text',
                          zIndex: 5,
                        }}
                      />

                      {/* Field Overlays */}
                      {getFieldsByPage(currentPage).map(field => {
                        const scaledWidth = field.width * containerScale;
                        const scaledHeight = field.height * containerScale;
                        const scaledFontSize = Math.max(8, 11 * containerScale);
                        const scaledIconSize = Math.max(12, 14 * containerScale);
                        const borderWidth = Math.max(1, 2 * containerScale);

                        return (
                          <div
                            key={field.id}
                            style={{
                              position: 'absolute',
                              left: `${field.x * 100}%`,
                              top: `${field.y * 100}%`,
                              width: `${scaledWidth}px`,
                              height: `${scaledHeight}px`,
                              border: field.filled ? `${borderWidth}px solid #10b981` : `${borderWidth}px dashed #ef4444`,
                              backgroundColor: field.filled ? 'rgba(16, 185, 129, 0.1)' : 'rgba(239, 68, 68, 0.1)',
                              cursor: draggingFieldId === field.id && isDragging ? 'grabbing' : 'grab',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: `${scaledFontSize}px`,
                              color: field.filled ? '#10b981' : '#ef4444',
                              fontWeight: '500',
                              zIndex: draggingFieldId === field.id ? 20 : 10,
                              userSelect: 'none',
                            }}
                            onMouseDown={(e) => handleFieldMouseDown(e, field.id)}
                          >
                            <div className="pointer-events-none">
                              {field.filled ? (
                                <div className="flex items-center gap-1">
                                  <span>✓</span>
                                </div>
                              ) : field.type === 'signature' ? (
                                <div className="flex flex-col items-center justify-center">
                                  <span style={{ fontSize: `${scaledFontSize}px`, marginBottom: '2px' }}>
                                    Sign
                                  </span>
                                  <DownloadIcon style={{ fontSize: scaledIconSize }} />
                                </div>
                              ) : (
                                <div className="flex items-center justify-center">
                                  <span style={{ fontSize: `${scaledFontSize}px` }}>
                                    Text
                                  </span>
                                </div>
                              )}
                            </div>
                            <button
                              onMouseDown={(e) => {
                                e.stopPropagation();
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                removeField(field.id);
                              }}
                              className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center hover:bg-red-600"
                              style={{ fontSize: '12px' }}
                            >
                              ×
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  </div>
                </div>
              </div>
            )}

            {/* Footer */}
            <div className="bg-white border-t border-gray-200 px-6 py-4 shrink-0">
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-4">
                  {/* Keep a copy of the signed document on the employee profile */}
                  <label className="flex items-center gap-2 cursor-pointer">
                    <div className="relative">
                      <input
                        type="checkbox"
                        checked={saveToProfile}
                        onChange={(e) => setSaveToProfile(e.target.checked)}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-300 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-violet-700"></div>
                    </div>
                    <span className="text-sm font-medium text-gray-700">Keep a copy on the employee's profile</span>
                  </label>
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={handleCancel}
                    className="px-6 py-2.5 border border-gray-300 hover:bg-gray-50 text-gray-700 rounded-lg font-medium transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSave}
                    disabled={!pdfPreviewUrl || placedFields.length === 0}
                    className={`px-6 py-2.5 rounded-lg font-medium transition-colors ${
                      !pdfPreviewUrl || placedFields.length === 0
                        ? 'bg-slate-300 text-slate-500 cursor-not-allowed'
                        : 'bg-violet-600 hover:bg-violet-700 text-white'
                    }`}
                  >
                    Save Signature Block
                  </button>
                </div>
              </div>
            </div>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center">
            <LoadSpinner />
          </div>
        )}

        {/* Signature Pad Modal */}
        <Modal
          open={showSignaturePad}
          onClose={() => setShowSignaturePad(false)}
          aria-labelledby="signature-pad-modal"
        >
          <Box sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 500,
            maxWidth: '90%',
            bgcolor: "background.paper",
            borderRadius: "12px",
            boxShadow: 24,
            p: 0,
          }}>
            <div className="bg-white rounded-lg p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-semibold text-slate-900">Draw Your Signature</h3>
                <button onClick={() => setShowSignaturePad(false)} className="text-slate-500 hover:text-slate-700">
                  <CloseIcon />
                </button>
              </div>
              <div className="border border-slate-300 rounded-lg mb-4 flex justify-center">
                <SignatureCanvas
                  ref={sigCanvas}
                  canvasProps={{
                    width: 450,
                    height: 200,
                    className: 'signature-canvas'
                  }}
                />
              </div>
              <div className="flex justify-between">
                <button
                  onClick={() => sigCanvas.current?.clear()}
                  className="px-4 py-2.5 bg-slate-200 hover:bg-slate-300 text-slate-700 rounded-lg font-medium transition-colors"
                >
                  Clear
                </button>
                <div className="flex gap-2">
                  <button
                    onClick={() => {
                      setShowSignaturePad(false);
                      setActiveFieldId(null);
                    }}
                    className="px-4 py-2.5 border border-gray-300 hover:bg-gray-50 text-gray-700 rounded-lg font-medium transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={saveSignatureToField}
                    className="px-4 py-2.5 bg-violet-600 hover:bg-violet-700 text-white rounded-lg font-medium transition-colors"
                  >
                    Save
                  </button>
                </div>
              </div>
            </div>
          </Box>
        </Modal>

        {/* Text Input Modal */}
        <Modal
          open={showTextInput}
          onClose={() => setShowTextInput(false)}
          aria-labelledby="text-input-modal"
        >
          <Box sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 500,
            maxWidth: '90%',
            bgcolor: "background.paper",
            borderRadius: "12px",
            boxShadow: 24,
            p: 0,
          }}>
            <div className="bg-white rounded-lg p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-semibold text-slate-900">Enter Text</h3>
                <button onClick={() => setShowTextInput(false)} className="text-slate-500 hover:text-slate-700">
                  <CloseIcon />
                </button>
              </div>
              <textarea
                value={textInputValue}
                onChange={(e) => setTextInputValue(e.target.value)}
                placeholder="Enter text for this field..."
                className="w-full border border-slate-300 rounded-lg p-3 mb-4 focus:border-violet-700 focus:outline-none"
                rows={4}
              />
              <div className="flex justify-end gap-2">
                <button
                  onClick={() => {
                    setShowTextInput(false);
                    setActiveFieldId(null);
                    setTextInputValue('');
                  }}
                  className="px-4 py-2.5 border border-gray-300 hover:bg-gray-50 text-gray-700 rounded-lg font-medium transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={saveTextToField}
                  className="px-4 py-2.5 bg-violet-600 hover:bg-violet-700 text-white rounded-lg font-medium transition-colors"
                >
                  Save
                </button>
              </div>
            </div>
          </Box>
        </Modal>
      </Box>
    </Modal>
  );
}

export default SignatureModal;
